import { useTranslations } from 'next-intl';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import isToday from 'date-fns/isToday';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Transition,
} from '@headlessui/react';
import { DisclosureIcon } from '@/component/Icons/DisclosureIcon';

import { Button } from '@/component/Form/Button';
import { Modal } from '@/component/Modal';
import { getFormatValue } from '../price_utils';

export function PaymentScheduleModal({
  price,
  isMonth,
  currencyTranslation,
  show,
  close,
}) {
  const t = useTranslations();

  const header = (
    <div
      className="text-center flex-1 justify-center flex gap-1 items-center max-md:py-2"
      data-testid="amenities-modal-header"
    >
      <div className="text-center text-slate-700 max-md:text-base md:text-2xl font-semibold leading-[30px] tracking-tight">
        {t('apartment.payment-schedule.title')}
      </div>
    </div>
  );
  const footer = (
    <Button onClick={close} theme="text" size="sm">
      {t('apartment.payment-schedule.button.hide')}
    </Button>
  );

  const hasDiscount = price?.discount?.totalPrice < price?.regular?.totalPrice;
  const titleMoneyPerUnitValue = getFormatValue(price?.discount?.price, ' ');
  const feeValue = getFormatValue(
    price?.cleaningFee,
    isMonth ? ' ' : currencyTranslation
  );
  const discountedMoneyValue = getFormatValue(
    price?.discount?.totalPrice,
    currencyTranslation
  );
  const discountedValue = getFormatValue(
    price?.regular?.totalPrice - price?.discount?.totalPrice,
    currencyTranslation
  );

  return (
    <Modal
      header={header}
      footer={footer}
      show={show}
      close={close}
      footerClass="md:hidden"
      closeClass="max-md:hidden"
      bodyClass="md:!p-8"
    >
      <div>
        <div className="flex flex-col items-center gap-1 pb-8">
          <b className="text-center text-slate-700 text-xl font-semibold leading-normal tracking-[0.1px]">
            {t('apartment.payment-schedule.header.title.before')}:{' '}
            {titleMoneyPerUnitValue}{' '}
            {t('apartment.payment-schedule.header.title.after')}
          </b>
          {!price.paymentSchedule?.length && feeValue && (
            <b className="text-center text-slate-700 text-xl font-semibold leading-normal tracking-[0.1px]">
              {t('apartment.payment-schedule.list.body.item1')}: {feeValue}
            </b>
          )}
          <div className="text-slate-500 text-lg font-normal leading-7">
            {t('apartment.payment-schedule.header.desc')}:{' '}
            {discountedMoneyValue}
          </div>
          {hasDiscount && discountedValue && (
            <div className="text-primary-700 text-xl font-normal leading-[30px]">
              {t('apartment.payment-schedule.header.sub-desc')}:{' '}
              {discountedValue}
            </div>
          )}
        </div>
      </div>
      <div className="border-b border-slate-200">
        {(price.paymentSchedule || []).map((payment, i) => (
          <Disclosure key={i}>
            <DisclosureButton data-active={hasDiscount || payment.cleaningFee ? true : false} className="group flex max-md:gap-[18px] md:gap-6 w-full justify-between items-center py-[18px] lg:py-8 border-t border-slate-200 text-left text-sm font-medium">
              <div className="flex items-center justify-between w-full gap-[18px] text-slate-700 text-base sm:text-2xl max-md:font-semibold md:font-medium">
                <span>
                  {t('apartment.payment-schedule.list.title.before')}{' '}
                  {isToday(parseISO(payment.paymentDate))
                    ? t('apartment.payment-schedule.list.title.after')
                    : format(parseISO(payment.paymentDate), 'MMMM dd, yyyy')}
                </span>
                <div className="flex flex-col items-end">
                  <span className="text-right whitespace-nowrap">
                    {getFormatValue(
                      payment.discountedPrice,
                      currencyTranslation
                    )}
                  </span>
                  {hasDiscount && (
                    <span className="text-right text-primary-700 max-md:text-[14px] md:text-[16px] font-medium leading-[normal]">
                      {t('apartment.payment-schedule.list.desc')}{' '}
                      <br className="md:hidden" />
                      {getFormatValue(
                        payment.regularPrice - payment.discountedPrice,
                        currencyTranslation
                      )}
                    </span>
                  )}
                </div>
              </div>
              <DisclosureIcon className="text-slate-400 min-w-6 group-data-[open]:rotate-45 group-data-[open]:transform transition ease-linear delay-50" />
            </DisclosureButton>
            <Transition
              as="div"
              enter="transition-all duration-200 ease-linear"
              enterFrom="transform opacity-0"
              enterTo="transform opacity-100"
              leave="transition-all duration-200 ease-linear"
              leaveFrom="transform opacity-100"
              leaveTo="transform opacity-0"
            >
              <DisclosurePanel className="max-md:pb-6 md:pb-8 max-md:pr-[42px] md:pr-12 flex flex-col max-md:gap-[18px] md:gap-2">
                {payment.cleaningFee ? (
                  <div className="flex gap-2 justify-between text-slate-700 text-lg leading-7">
                    <span className="font-normal">
                      {t('apartment.payment-schedule.list.body.item1')}
                    </span>
                    <span className="font-medium text-right inline-flex">
                      {getFormatValue(payment.cleaningFee, currencyTranslation)}
                    </span>
                  </div>
                ) : null}
                {hasDiscount && (
                  <div className="flex gap-2 justify-between text-slate-700 text-lg leading-7">
                    <span className="font-normal">
                      {t('apartment.payment-schedule.list.body.item2')}
                    </span>
                    <span className="font-medium text-right text-primary-700">
                      -
                      {getFormatValue(
                        payment.regularPrice - payment.discountedPrice,
                        currencyTranslation
                      )}
                    </span>
                  </div>
                )}
              </DisclosurePanel>
            </Transition>
          </Disclosure>
        ))}
      </div>
    </Modal>
  );
}
